import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/lib/axiosInstance';
import { useToast } from '@/components/ToastProvider';
import { parseApiError } from '@/lib/utils';
import {
  Robot,
  ShieldWarning,
  Bug,
  Network,
  Plug,
  Shield,
  CheckCircle,
  X,
  ArrowsClockwise,
  PaperPlaneRight,
  Power,
  ChatCircleDots,
  SpinnerGap,
} from '@phosphor-icons/react';

// apiClient ya tiene baseURL=${BACKEND_URL}/api — paths relativos SIN /api.

// --- Mapa de tonos → estilos (coherente con ActionCard / SecurityRoadmap) ---
const TONE = {
  critical: {
    text: 'text-red-300',
    border: 'border-red-500/[0.28]',
    bg: 'bg-red-500/[0.10]',
    dot: 'bg-red-400',
    btn: 'border-red-500/40 text-red-300 hover:bg-red-500/10',
  },
  warning: {
    text: 'text-orange-300',
    border: 'border-orange-500/[0.25]',
    bg: 'bg-orange-500/[0.10]',
    dot: 'bg-orange-400',
    btn: 'border-orange-500/40 text-orange-300 hover:bg-orange-500/10',
  },
  info: {
    text: 'text-cyan-300',
    border: 'border-cyan-500/[0.22]',
    bg: 'bg-cyan-500/[0.10]',
    dot: 'bg-cyan-400',
    btn: 'border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10',
  },
  success: {
    text: 'text-emerald-300',
    border: 'border-emerald-500/[0.25]',
    bg: 'bg-emerald-500/[0.10]',
    dot: 'bg-emerald-400',
    btn: 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10',
  },
};

// --- Mapa de iconos (string del backend → componente phosphor) ---
const ICON = {
  'shield-alert': ShieldWarning,
  bug: Bug,
  network: Network,
  plug: Plug,
  shield: Shield,
  check: CheckCircle,
};

// --- Color de nota global (A..F) ---
const GRADE_COLOR = {
  A: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10',
  B: 'text-cyan-400 border-cyan-500/30 bg-cyan-500/10',
  C: 'text-amber-400 border-amber-500/30 bg-amber-500/10',
  D: 'text-orange-400 border-orange-500/30 bg-orange-500/10',
  F: 'text-red-400 border-red-500/30 bg-red-500/10',
};

const toneOf = (t) => TONE[t] || TONE.info;
const iconOf = (i) => ICON[i] || Shield;

const CopilotWidget = () => {
  const navigate = useNavigate();
  const toast = useToast();

  // Fuente de verdad = backend; localStorage solo para respuesta instantánea al montar.
  const [enabled, setEnabled] = useState(() => {
    const cached = localStorage.getItem('copilot_enabled');
    return cached === null ? true : cached === 'true';
  });
  const [llmAvailable, setLlmAvailable] = useState(false);
  const [open, setOpen] = useState(false);
  const [togglingEnabled, setTogglingEnabled] = useState(false);

  const [guidance, setGuidance] = useState(null);
  const [guidanceLoading, setGuidanceLoading] = useState(false);
  const [guidanceError, setGuidanceError] = useState(null);

  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const chatEndRef = useRef(null);

  // Al montar: sincronizar estado real (enabled + disponibilidad LLM) con backend.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/copilot/settings');
        if (cancelled) return;
        setEnabled(!!res.data?.enabled);
        setLlmAvailable(!!res.data?.llm_available);
        localStorage.setItem('copilot_enabled', String(!!res.data?.enabled));
      } catch {
        // silencio-ok: precarga del ajuste; se mantiene el valor cacheado y la guía avisa si su carga falla
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Cierre con Escape mientras el panel está abierto.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Autoscroll del chat al último mensaje.
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatSending]);

  const loadGuidance = useCallback(async () => {
    setGuidanceLoading(true);
    setGuidanceError(null);
    try {
      const res = await apiClient.get('/copilot/guidance');
      setGuidance(res.data);
    } catch {
      setGuidanceError('No se pudo cargar la guía del copiloto.');
    } finally {
      setGuidanceLoading(false);
    }
  }, []);

  // Cargar la guía al abrir el panel (si está activado y aún no hay datos).
  useEffect(() => {
    if (open && enabled && !guidance && !guidanceLoading && !guidanceError) {
      loadGuidance();
    }
  }, [open, enabled, guidance, guidanceLoading, guidanceError, loadGuidance]);

  const setEnabledRemote = useCallback(async (next) => {
    setTogglingEnabled(true);
    // Optimista + persistencia local inmediata.
    setEnabled(next);
    localStorage.setItem('copilot_enabled', String(next));
    try {
      const res = await apiClient.patch('/copilot/settings', { enabled: next });
      setEnabled(!!res.data?.enabled);
      setLlmAvailable(!!res.data?.llm_available);
      localStorage.setItem('copilot_enabled', String(!!res.data?.enabled));
    } catch (err) {
      // Revertir si falla la persistencia en backend, y decirlo.
      setEnabled(!next);
      localStorage.setItem('copilot_enabled', String(!next));
      toast.error(parseApiError(err.response?.data?.detail, 'No se pudo guardar el ajuste del copiloto'));
    } finally {
      setTogglingEnabled(false);
    }
  }, [toast]);

  const goTo = (route) => {
    setOpen(false);
    if (route) navigate(route);
  };

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatSending) return;
    setChatInput('');
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setChatSending(true);
    try {
      const res = await apiClient.post('/copilot/chat', { message: text });
      setMessages((prev) => [...prev, { role: 'assistant', text: res.data?.reply || '…' }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: 'No pude responder ahora mismo. Inténtalo de nuevo.' },
      ]);
    } finally {
      setChatSending(false);
    }
  };

  const summary = guidance?.summary;
  const items = Array.isArray(guidance?.items) ? guidance.items : [];

  return (
    // `pointer-events-none` en el CONTENEDOR: con el copiloto cerrado, la caja
    // fija seguia capturando clics y tapaba los botones reales de la esquina
    // inferior derecha (Playwright: "<div ...> intercepts pointer events" al
    // borrar un mapeo). Los hijos que si deben ser pulsables lo recuperan con
    // `pointer-events-auto` (el panel ya lo alterna segun `open`).
    <div className="fixed z-[60] bottom-20 right-4 lg:bottom-6 lg:right-6 flex flex-col items-end gap-3 print:hidden pointer-events-none">
      {/* Panel */}
      <div
        role="dialog"
        aria-modal="false"
        aria-label="Copiloto Vektor"
        aria-hidden={!open}
        className={`w-[360px] max-w-[calc(100vw-2rem)] origin-bottom-right rounded-[8px] border border-v4-line bg-v4-panel shadow-2xl shadow-black/40 transition-all duration-200 ${
          open
            ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 scale-95 translate-y-2 pointer-events-none'
        }`}
      >
        {/* Cabecera */}
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-v4-line-2">
          <div className="w-8 h-8 rounded-full bg-v4-accent-bg border border-v4-accent-line flex items-center justify-center flex-shrink-0">
            <Robot className="h-4 w-4 text-v4-accent" weight="fill" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold text-v4-txt leading-tight">Copiloto Vektor</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-v4-faint">
              {enabled ? 'Asistente activo' : 'Desactivado'}
            </p>
          </div>
          {enabled && (
            <button
              type="button"
              onClick={loadGuidance}
              disabled={guidanceLoading}
              aria-label="Actualizar guía"
              title="Actualizar"
              className="w-7 h-7 rounded-[5px] border border-v4-line-2 flex items-center justify-center text-v4-dim hover:border-v4-line hover:text-v4-txt transition-colors disabled:opacity-50"
            >
              <ArrowsClockwise className={`h-3.5 w-3.5 ${guidanceLoading ? 'animate-spin' : ''}`} />
            </button>
          )}
          <button
            type="button"
            onClick={() => setEnabledRemote(!enabled)}
            disabled={togglingEnabled}
            aria-label={enabled ? 'Desactivar copiloto' : 'Reactivar copiloto'}
            title={enabled ? 'Desactivar' : 'Reactivar'}
            className={`w-7 h-7 rounded-[5px] border flex items-center justify-center transition-colors disabled:opacity-50 ${
              enabled
                ? 'border-v4-line-2 text-v4-dim hover:border-red-500/40 hover:text-red-300'
                : 'border-v4-accent-line text-v4-accent hover:bg-v4-accent-bg'
            }`}
          >
            <Power className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Cerrar panel"
            title="Cerrar"
            className="w-7 h-7 rounded-[5px] border border-v4-line-2 flex items-center justify-center text-v4-dim hover:border-v4-line hover:text-v4-txt transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Cuerpo */}
        {!enabled ? (
          <div className="px-4 py-6 text-center space-y-3">
            <p className="text-[12.5px] text-v4-dim leading-relaxed">
              El copiloto está desactivado. Actívalo para recibir bienvenida, guía y accesos directos a tus tareas de
              seguridad.
            </p>
            <button
              type="button"
              onClick={() => setEnabledRemote(true)}
              disabled={togglingEnabled}
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] px-4 py-2 rounded-[5px] border border-v4-accent-line bg-v4-accent-bg text-v4-accent hover:bg-v4-accent hover:text-v4-bg transition-colors disabled:opacity-50"
            >
              <Power className="h-3.5 w-3.5" />
              Reactivar copiloto
            </button>
          </div>
        ) : (
          <div className="max-h-[min(70vh,520px)] overflow-y-auto">
            {/* Saludo + nota global */}
            <div className="px-4 pt-3 pb-2">
              {summary && (summary.score != null || summary.grade) && (
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded border ${
                      GRADE_COLOR[summary.grade] || GRADE_COLOR.C
                    }`}
                  >
                    {summary.grade && <span className="font-semibold">{summary.grade}</span>}
                    {summary.score != null && <span className="tabular-nums">{summary.score}/100</span>}
                  </span>
                  {summary.total_cards != null && (
                    <span className="font-mono text-[10px] text-v4-faint tabular-nums">
                      {summary.cards_done ?? 0}/{summary.total_cards} tareas
                    </span>
                  )}
                </div>
              )}
              {guidance?.greeting && (
                <p className="text-[13px] text-v4-dim leading-relaxed">{guidance.greeting}</p>
              )}
            </div>

            {/* Estados de carga / error */}
            {guidanceLoading && !guidance && (
              <div className="px-4 py-6 flex items-center justify-center gap-2 text-v4-faint">
                <SpinnerGap className="h-4 w-4 animate-spin" />
                <span className="font-mono text-[11px] uppercase tracking-[0.16em]">Cargando guía…</span>
              </div>
            )}
            {guidanceError && (
              <div className="px-4 py-4">
                <div className="flex items-start gap-2 px-3 py-2 bg-amber-500/[0.06] border border-amber-500/20 rounded-[5px]">
                  <ShieldWarning className="h-3.5 w-3.5 text-amber-300 flex-shrink-0 mt-0.5" />
                  <p className="text-[12px] text-v4-dim leading-relaxed">{guidanceError}</p>
                </div>
              </div>
            )}

            {/* Lista de items */}
            {items.length > 0 && (
              <div className="px-4 pb-2 space-y-2">
                {items.map((it) => {
                  const tone = toneOf(it.tone);
                  const Icon = iconOf(it.icon);
                  return (
                    <div
                      key={it.id}
                      className={`rounded-[6px] border ${tone.border} ${tone.bg} p-3`}
                    >
                      <div className="flex items-start gap-2.5">
                        <div
                          className={`w-7 h-7 rounded-[5px] bg-v4-panel border border-v4-line-2 flex items-center justify-center flex-shrink-0`}
                        >
                          <Icon className={`h-4 w-4 ${tone.text}`} weight="fill" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`text-[12.5px] font-medium leading-snug ${tone.text}`}>{it.title}</p>
                          {it.message && (
                            <p className="text-[12px] text-v4-dim leading-relaxed mt-0.5">{it.message}</p>
                          )}
                          {it.action_label && it.action_route && (
                            <button
                              type="button"
                              onClick={() => goTo(it.action_route)}
                              className={`mt-2 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] px-2.5 py-1 rounded-[4px] border transition-colors ${tone.btn}`}
                            >
                              {it.action_label}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!guidanceLoading && !guidanceError && items.length === 0 && guidance && (
              <div className="px-4 pb-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/[0.06] border border-emerald-500/20 rounded-[5px]">
                  <CheckCircle className="h-4 w-4 text-emerald-400 flex-shrink-0" weight="fill" />
                  <p className="text-[12px] text-v4-dim">Todo en orden por ahora. Sin acciones urgentes.</p>
                </div>
              </div>
            )}

            {/* Chat */}
            <div className="border-t border-v4-line-2 mt-1">
              {messages.length > 0 && (
                <div className="px-4 py-3 space-y-2 max-h-56 overflow-y-auto">
                  {messages.map((m, idx) => (
                    <div
                      key={idx}
                      className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-[8px] px-3 py-2 text-[12px] leading-relaxed ${
                          m.role === 'user'
                            ? 'bg-v4-accent-bg border border-v4-accent-line text-v4-txt'
                            : 'bg-v4-panel-2 border border-v4-line-2 text-v4-dim'
                        }`}
                      >
                        {m.text}
                      </div>
                    </div>
                  ))}
                  {chatSending && (
                    <div className="flex justify-start">
                      <div className="bg-v4-panel-2 border border-v4-line-2 rounded-[8px] px-3 py-2 text-v4-faint">
                        <SpinnerGap className="h-3.5 w-3.5 animate-spin" />
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
              )}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  sendChat();
                }}
                className="flex items-center gap-2 px-3 py-3"
              >
                <ChatCircleDots className="h-4 w-4 text-v4-faint flex-shrink-0" />
                <input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder={llmAvailable ? 'Pregúntame algo…' : 'Escribe tu duda…'}
                  aria-label="Mensaje para el copiloto"
                  className="flex-1 min-w-0 bg-v4-panel-2 border border-v4-line-2 rounded-[5px] px-3 py-1.5 text-[12px] text-v4-txt focus:border-v4-line focus:outline-none placeholder:text-v4-faint"
                />
                <button
                  type="submit"
                  disabled={!chatInput.trim() || chatSending}
                  aria-label="Enviar mensaje"
                  className="w-8 h-8 rounded-[5px] border border-v4-accent-line bg-v4-accent-bg text-v4-accent flex items-center justify-center hover:bg-v4-accent hover:text-v4-bg transition-colors disabled:opacity-40 disabled:hover:bg-v4-accent-bg disabled:hover:text-v4-accent"
                >
                  <PaperPlaneRight className="h-4 w-4" weight="fill" />
                </button>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Burbuja flotante */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Cerrar copiloto' : 'Abrir copiloto'}
        aria-expanded={open}
        className={`pointer-events-auto relative w-14 h-14 rounded-full flex items-center justify-center shadow-lg shadow-black/40 border transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-v4-accent-line ${
          enabled
            ? 'bg-v4-accent border-v4-accent-line text-v4-bg'
            : 'bg-v4-panel border-v4-line-2 text-v4-faint'
        }`}
      >
        {open ? <X className="h-6 w-6" weight="bold" /> : <Robot className="h-7 w-7" weight="fill" />}
        {/* Indicador de acciones pendientes */}
        {!open && enabled && items.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 border-2 border-v4-bg text-white text-[10px] font-bold flex items-center justify-center tabular-nums">
            {items.length}
          </span>
        )}
      </button>
    </div>
  );
};

export default CopilotWidget;
